import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  inject,
  Input,
  OnDestroy,
  Output,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { LearnosityAssessmentApiService, LearnosityItemsConfigInterface, QuestionInterface } from '@campus/dal';
import '@diekeure/dcr-components/dcr-filled-button.js';
import { Subject, take } from 'rxjs';
import { learnosityScriptVersion } from '../../constants/learnosity-version';
import { RESULT_UPDATER_TOKEN, ResultUpdater } from '../../interfaces';
import {
  AttachGradingItemPayloadInterface,
  GradingAppInterface,
  GradingResultResponseInterface,
} from '../../interfaces/learnosity.interface';
import { LEARNOSITY_WINDOW } from '../../services/learnosity.window';

@Component({
  standalone: false,
  selector: 'campus-grading',
  templateUrl: './grading.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GradingComponent implements OnDestroy, AfterViewInit {
  @Input() config: LearnosityItemsConfigInterface;
  @Output() closed: EventEmitter<boolean> = new EventEmitter();
  @Output() componentsLoaded: EventEmitter<void> = new EventEmitter();

  @HostBinding('class') defaultClasses = ['p-s', 'stack', 'h-screen', 'overflow-hidden'];

  @ViewChild('learnosity_grading') gradingContainer: ElementRef;

  private el: ElementRef = inject(ElementRef);
  private renderer: Renderer2 = inject(Renderer2);
  private window: Window = inject(LEARNOSITY_WINDOW);
  private learnosityService = inject(LearnosityAssessmentApiService);
  private resultUpdater: ResultUpdater = inject(RESULT_UPDATER_TOKEN, { optional: true });
  private items: AttachGradingItemPayloadInterface[];

  private gradingApp: GradingAppInterface;
  private event$ = new Subject<{ type: string; payload?: any }>();

  public async ngAfterViewInit() {
    await this.setupLearnosityScript();
  }

  async save() {
    try {
      const result = await this.gradingApp.save();
      await this.saveResultsWithChanges(result);
      this.close();
    } catch (e) {
      console.error(e);
    }
  }

  close() {
    this.closed.emit();
  }

  public ngOnDestroy(): void {
    this.event$.next({
      type: 'dk:component:destroy',
      payload: {},
    });
  }

  private async saveResultsWithChanges(result: GradingResultResponseInterface[]) {
    const resultsByResponseIdByItemId = result.reduce((acc, activity) => {
      activity.updated_scores.forEach((item) => {
        item.response_scores.forEach((question) => {
          const { score, max_score } = question.grader_scores[0];
          acc[item.item_id] ||= {};
          acc[item.item_id][question.response_id] = { score, maxScore: max_score };
        });
      });
      return acc;
    }, {});

    const activities = this.gradingApp.getAttachedItems();

    const { score, maxScore } = activities.reduce(
      (acc, activity) => {
        activity.items.forEach((item) => {
          const updatedScoresByQuestionId = resultsByResponseIdByItemId[item.reference] ?? {};
          item.grades.forEach((question) => {
            const updatedScore = updatedScoresByQuestionId[question.responseId];
            if (updatedScore) {
              acc.score += updatedScore.score;
              acc.maxScore += updatedScore.maxScore;
            } else {
              acc.score += question.score.score;
              acc.maxScore += question.score.maxScore;
            }
          });
        });

        return acc;
      },
      { score: 0, maxScore: 0 }
    );

    const currentResult = this.config.result;
    currentResult.score = Math.round((score / maxScore) * 100);

    const config = {
      request: {
        user_id: this.config.result.person.uuid,
        activity_id: this.config.result.eduContent.publishedEduContentMetadata.sourceRef,
        session_id: this.config.result.learnositySessionId,
      },
    };

    this.learnosityService.getIntermediateReport(config).pipe(take(1)).subscribe();
    this.resultUpdater?.(currentResult, true);
  }

  private async setupLearnosityScript(): Promise<void> {
    await this.createAndAppendLearnosityScriptAsync();
    await this.initializeLearnosity();
  }

  private createAndAppendLearnosityScriptAsync(): Promise<void> {
    return new Promise((resolve) => {
      const script = this.renderer.createElement('script');
      script.id = 'learnosity_grading_script_id';
      script.type = 'text/javascript';
      script.src = `//grading-ie.learnosity.com/?${learnosityScriptVersion}`;
      script.onload = () => resolve();
      this.renderer.appendChild(this.el.nativeElement.ownerDocument.head, script);
    });
  }

  private async initializeLearnosity() {
    if (!this.config) return;

    const gradingConfig = JSON.parse(JSON.stringify(this.config));
    this.gradingApp = await this.window.LearnosityGrading.init(gradingConfig);

    // Emit that components are loaded after initialization
    this.componentsLoaded.emit();

    this.items = Array.from(
      new Set(
        (
          gradingConfig.result.eduContent.questions ??
          gradingConfig.result.eduContent.publishedEduContentMetadata.questions
        ).map((question: QuestionInterface) => question.itemId)
      )
    ).map(
      (itemId) =>
        ({
          userId: gradingConfig.result.person.uuid,
          sessionId: gradingConfig.result.learnositySessionId,
          item: itemId,
        }) as AttachGradingItemPayloadInterface
    );

    const element = this.gradingContainer.nativeElement;

    for (const item of this.items) {
      try {
        await this.gradingApp.attachItem(item, element);
      } catch (e) {
        console.warn('Item could not be loaded', item.item, 'the item has probably changed since the last try');
      }
    }

    if (this.config.request.state === 'review') return;
    this.window.gradingApp = this.gradingApp;
  }
}
